import { useId } from '@radix-ui/react-id';
import cn from 'classnames';
import ReactMarkdown from 'react-markdown';
import { Tooltip } from 'react-scale/components';
import rehypeRaw from 'rehype-raw';

const Comparison = ({ product, inverted, comparison }) => {
  const badgesWrapperStyles = cn('flex min-h-[89px]', inverted && 'flex-row-reverse');
  const badgeStyles = cn(
    'bg-gradient-x p-[1px] flex-grow w-1/2',
    inverted ? 'rounded-r-lg' : 'rounded-l-lg'
  );
  const badgeInnerStyles = cn(
    'flex flex-col items-center justify-center h-full py-4 space-y-2 bg-black px-7',
    inverted ? 'rounded-r-lg' : 'rounded-l-lg'
  );
  const badgeCompetitorStyles = cn(
    'flex flex-col items-center justify-center py-4 space-y-2 border-t border-b border-tundora px-7 h-full',
    inverted ? 'border-l rounded-l-lg' : 'border-r rounded-r-lg'
  );

  return (
    <div>
      <div className="mb-32 space-y-10 lg:hidden">
        {comparison?.features.map((feature, index) => (
          <div key={feature.id} className="space-y-10">
            <div>
              <ReactMarkdown
                rehypePlugins={[rehypeRaw]}
                className="mb-6 text-xs leading-6 sm:text-base sm:leading-8"
              >
                {feature.title}
              </ReactMarkdown>
              <div className={badgesWrapperStyles}>
                <div className={badgeStyles}>
                  <div className={badgeInnerStyles}>
                    {typeof feature.ourProduct !== 'string' && (
                      <p className="text-xs font-medium leading-6 text-center text-gradient sm:text-base sm:leading-8 whitespace-nowrap">
                        {product}
                      </p>
                    )}
                    <span className="px-2 py-1">
                      {feature ? (
                        typeof feature.ourProduct === 'string' ? (
                          <p className="text-white text-xs">{feature.competitor}</p>
                        ) : (
                          <CheckMarkGradient />
                        )
                      ) : (
                        <Cross />
                      )}
                    </span>
                  </div>
                </div>
                <div className="w-1/2">
                  <div className={badgeCompetitorStyles}>
                    {typeof feature.competitor !== 'string' && (
                      <p className="text-xs leading-6 text-center sm:text-base sm:leading-8">
                        {comparison?.competitor ?? ''}
                      </p>
                    )}
                    <span className="px-2 py-1 text-[#A3A3A3]">
                      {feature.competitor ? (
                        typeof feature.competitor === 'string' ? (
                          <p className="text-white text-xs">{feature.competitor}</p>
                        ) : (
                          <CheckMark />
                        )
                      ) : (
                        <Cross />
                      )}
                    </span>
                  </div>
                </div>
              </div>
              {feature?.disclaimer && (
                <div className="flex text-[#A3A3A3] items-center mt-6 space-x-2">
                  <Info width="15" height="15" />
                  <p className="text-[10px] leading-6">{feature.disclaimer}</p>
                </div>
              )}
            </div>
            {index !== comparison?.features.length - 1 && (
              <hr className="bg-tundora mb-10 h-[1px] border-0" />
            )}
          </div>
        ))}
      </div>

      <table className="hidden w-full lg:table">
        <thead>
          <tr className="border-b border-neutral-700">
            <th> </th>
            {!inverted && (
              <th className="w-1/5 text-xl font-medium leading-8 bg-gradient-x rounded-t-xl whitespace-nowrap pr-[1px] pb-0 pt-[1px] h-10">
                <div className="bg-neutral-900 rounded-t-xl h-full flex justify-center items-center">
                  {product}
                </div>
              </th>
            )}
            <th className="w-1/5 py-8 text-lg font-normal leading-8">
              {comparison?.competitor ?? ''}
            </th>
            {inverted && (
              <th className="w-1/5 py-8 text-xl font-medium leading-8 bg-gradient-x rounded-t-xl whitespace-nowrap">
                {product}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {comparison?.features.map(feature => (
            <tr key={feature.id} className="border-b border-neutral-700">
              <td className="py-8 pr-6 text-sm lg:text-lg">
                <ReactMarkdown rehypePlugins={[rehypeRaw]}>{feature.title}</ReactMarkdown>
              </td>
              {!inverted && (
                <td className="bg-gradient-x py-0 h-20">
                  <div className="flex items-center justify-center bg-neutral-900 py-8 h-full">
                    {feature.ourProduct ? (
                      typeof feature.competitor === 'string' ? (
                        <p className="text-white lg:text-lg">{feature.competitor}</p>
                      ) : (
                        <CheckMarkGradient />
                      )
                    ) : (
                      <Cross />
                    )}
                  </div>
                </td>
              )}
              <td className="py-8">
                <div className="flex justify-center items-center text-[#A3A3A3]">
                  {feature.competitor ? (
                    typeof feature.competitor === 'string' ? (
                      <p className="text-white lg:text-lg">{feature.competitor}</p>
                    ) : (
                      <CheckMark />
                    )
                  ) : feature?.disclaimer ? (
                    <Tooltip
                      content={
                        <div className="bg-neutral-800 rounded-[4px] p-4 text-white">
                          {feature.disclaimer}
                        </div>
                      }
                      arrow={{
                        align: 'center',
                        width: 14,
                        height: 8,
                        className: 'fill-current text-neutral-800'
                      }}
                    >
                      <div className="flex justify-center p-4 -my-4 items-center relative hover:text-[#F6F5FA] transition-colors duration-300">
                        <Cross />
                        <div className="absolute transform translate-x-5 top-1">
                          <Info />
                        </div>
                      </div>
                    </Tooltip>
                  ) : (
                    <Cross />
                  )}
                </div>
              </td>
              {inverted && (
                <td className="bg-neutral-900 py-8">
                  <div className="flex items-center justify-center">
                    {feature.ourProduct ? (
                      typeof feature.competitor === 'string' ? (
                        <p className="text-white lg:text-lg">{feature.competitor}</p>
                      ) : (
                        <CheckMarkGradient />
                      )
                    ) : (
                      <Cross />
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
          <tr>
            <td> </td>
            {!inverted && (
              <td className="bg-gradient-x h-9 rounded-b-xl pb-[1px] pt-0 px-[1px]">
                <div className="bg-neutral-900 h-full rounded-b-xl"> </div>
              </td>
            )}
            <td> </td>
            {inverted && (
              <td className="bg-gradient-x h-9 rounded-b-xl pb-[1px] pt-0 px-[1px]">
                <div className="bg-neutral-900 h-full rounded-b-xl"> </div>
              </td>
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const CheckMark = () => (
  <svg width="24" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m.667 8.973 1.334-1.357 6.693 6.67L22.057.9l1.38 1.357L8.694 17 .667 8.973Z"
      fill="#A3A3A3"
    />
  </svg>
);

const CheckMarkGradient = () => {
  const id = useId();

  return (
    <svg width="24" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="m.667 8.973 1.334-1.357 6.693 6.67L22.057.9l1.38 1.357L8.694 17 .667 8.973Z"
        fill={`url(#${id})`}
      />
      <defs>
        <linearGradient id={id} x1="12.5" y1="-6" x2="12.5" y2="24" gradientUnits="userSpaceOnUse">
          <stop stopColor="#C871D3" />
          <stop offset="1" stopColor="#77C6F3" />
        </linearGradient>
      </defs>
    </svg>
  );
};

const Cross = () => (
  <svg width="17" height="17" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="m1.816.1 6.67 6.67L15.179.1l1.357 1.357-6.693 6.67 6.693 6.716-1.357 1.334-6.693-6.67-6.67 6.67-1.357-1.334 6.693-6.716-6.693-6.67L1.816.1Z"
      fill="currentColor"
    />
  </svg>
);

const Info = () => (
  <svg width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M7.857 3.696c.266 0 .486.088.66.262.175.167.262.38.262.638a.895.895 0 0 1-.922.91.934.934 0 0 1-.671-.262.881.881 0 0 1-.262-.648c0-.258.087-.471.262-.638a.934.934 0 0 1 .671-.262ZM7.05 6.924h1.605V11.6H7.049V6.924Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M7.765 1.2a6.565 6.565 0 1 0 0 13.13 6.565 6.565 0 0 0 0-13.13ZM0 7.765a7.765 7.765 0 1 1 15.53 0 7.765 7.765 0 0 1-15.53 0Z"
      fill="#A3A3A3"
    />
  </svg>
);

export default Comparison;
